import * as React from 'react'
import * as domain from '@packages/domain'
import { E, O, pipe, RTE, TE } from '@packages/prelude'
import { ADTType, makeADT, ofType } from '@morphic-ts/adt'
import { useApi } from '~/api'

interface Recording {
  type: 'Recording'
  id: number
  audio: Blob
}

interface Name {
  type: 'Name'
  id: number
  name: string
}
interface TemplateName {
  type: 'TemplateName'
  id: string
  name: string
}

interface StarGreeting {
  type: 'StarGreeting'
  id: number
  greeting: string
}

interface AIGreeting {
  type: 'AIGreeting'
  id: number
  audio: Blob
}

interface UserImage {
  type: 'UserImage'
  id: number
  image: File
}

const isSupportedImageMime = (mime: string): boolean => mime === 'image/jpeg' || mime === 'image/jpg' || mime === 'image/png'

const validateImageFile = (file: File): E.Either<Error, File> =>
  pipe(
    file.type,
    E.fromPredicate(isSupportedImageMime, () => new Error(`Unsupported image type: ${file.type}`)),
    E.map(() => file)
  )

export const PreviewData = makeADT('type')({
  Recording: ofType<Recording>(),
  Name: ofType<Name>(),
  TemplateName: ofType<TemplateName>(),
  StarGreeting: ofType<StarGreeting>(),
  AIGreeting: ofType<AIGreeting>(),
  UserImage: ofType<UserImage>()
})

export type PreviewData = ADTType<typeof PreviewData>

const appendOne = (formData: FormData): RTE.ReaderTaskEither<PreviewData, Error, void> =>
  pipe(
    RTE.ask<PreviewData>(),
    RTE.chainTaskEitherK(
      PreviewData.matchStrict({
        Recording: ({ audio, id }) => TE.fromIO(() => formData.append(String(id), audio, 'rec.webm')),
        Name: ({ name, id }) => TE.fromIO(() => formData.append(String(id), name)),
        TemplateName: ({ name, id }) => TE.fromIO(() => formData.append(String(id), name)),
        StarGreeting: ({ greeting, id }) => TE.fromIO(() => formData.append(String(id), greeting)),
        AIGreeting: ({ audio, id }) => TE.fromIO(() => formData.append(String(id), audio, 'rec.mp3')),
        UserImage: ({ image, id }) =>
          pipe(
            validateImageFile(image),
            TE.fromEither,
            TE.chainIOK(file => () => formData.append(String(id), file, file.name))
          )
      })
    )
  )

export const appendPreviewDataToFormData = (
  formData: FormData
): RTE.ReaderTaskEither<ReadonlyArray<PreviewData>, Error, FormData> =>
  pipe(
    RTE.ask<ReadonlyArray<PreviewData>>(),
    RTE.chainW(
      RTE.traverseSeqArray(d =>
        pipe(
          appendOne(formData),
          RTE.local(() => d)
        )
      )
    ),
    RTE.map(() => formData)
  )

export interface UseIndiAudioPreviewResult {
  readonly generatePreview: (
    payload: ReadonlyArray<PreviewData>
  ) => TE.TaskEither<domain.Error.Type, domain.Api.PreviewToken.Type>
  readonly getStatus: (token: string) => TE.TaskEither<domain.Error.Type, O.Option<domain.Api.IAGenPreviewResponse.Type>>
  readonly reset: () => void
}

export const useIndiAudioPreview = (productId: number): UseIndiAudioPreviewResult => {
  const [generate, , reset] = useApi(domain.Api.IndiAudio.generatePreview)
  const [getStatusQuery] = useApi(domain.Api.IndiAudio.getPreviewStatus)

  const generatePreview = React.useCallback(
    (payload: ReadonlyArray<PreviewData>): TE.TaskEither<domain.Error.Type, domain.Api.PreviewToken.Type> =>
      pipe(
        appendPreviewDataToFormData(new FormData())(payload),
        TE.mapLeft(domain.Error.toUnknownError),
        TE.chain(formData => generate({ productId, formData }))
      ),
    [generate, productId]
  )

  const getStatus = React.useCallback(
    (token: string): TE.TaskEither<domain.Error.Type, O.Option<domain.Api.IAGenPreviewResponse.Type>> =>
      getStatusQuery({ token }),
    [getStatusQuery]
  )

  return { generatePreview, getStatus, reset }
}
